import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";
import * as WebBrowser from "expo-web-browser";
import React, { useEffect, useState } from "react";
import { Pressable, View, Text, TouchableOpacity } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { makeUrl } from "../globalUtils";
import AppInput from "./AppInput";
import Button from "@/components/settings/Button";
import Callout from "@/components/settings/Callout";
import { FieldLabel } from "@/components/settings/Field";
import useCopy from "@/components/settings/useCopy";
import { text as settingsText } from "@/components/settings/theme";
import { resetEvent } from "../http/events";
import { notify } from "./AppToast";
import useClipboardCallback from "@/components/hooks/useClipboardCallback";
import { useNavigation } from "@react-navigation/native";
import useNow from "@/components/hooks/useNow";
import tw from "twrnc";
import { t } from "@/localization";

const openLink = async (url) => {
  await WebBrowser.openBrowserAsync(url);
};

// Live events get the same tip as the event link on the Overview; a test's
// private link gets "open it on another device". Pass help={null} for links
// that aren't watch links (e.g. camera invites).
export const UrlToWatch = ({
  title,
  url,
  isTest,
  help = isTest ? "goLive.watchLink" : "overview.eventLink",
}) => {
  const { copy, isCopied } = useCopy();
  const copied = isCopied();

  return (
    <>
      <FieldLabel label={title} help={help} style={tw`mt-4`} />
      <AppInput
        value={url}
        disabled={true}
        labelStyle={tw`text-right`}
        rightComponent={
          <View style={tw`flex-row gap-2`}>
            <Pressable
              style={({ pressed }) => [{ opacity: pressed ? 0.3 : 1.0 }]}
              onPress={() => copy(url, "default", { toast: t("Link copied") })}
              accessibilityRole="button"
              accessibilityLabel={copied ? t("Copied") : t("Copy link")}
            >
              <Ionicons
                name={copied ? "checkmark" : "copy-outline"}
                size={24}
                style={copied ? tw`text-[#15803D]` : tw`text-[#8B8B8D]`}
              />
            </Pressable>
            <Pressable
              style={({ pressed }) => [{ opacity: pressed ? 0.3 : 1.0 }]}
              onPress={() => {
                openLink(url);
              }}
            >
              <Ionicons
                name="open-outline"
                size={24}
                style={tw`text-[#8B8B8D]`}
              />
            </Pressable>
          </View>
        }
      />
    </>
  );
};

const RtmpForm = ({ event, test_event, custom_domain }) => {
  const {
    is_published,
    stream_key,
    broadcaster,
    event_status,
    slug,
    is_test,
    displayStyle = "server_key",
    event_window_start_datetime_utc,
  } = event;
  const clipBoardCallback = useClipboardCallback();
  const [selectedTab, setActiveTab] = useState(!is_test ? "live" : "test");
  // A test event only has the test tab. is_test can arrive after the first
  // render (How to Go Live opened from a link), so don't trust the initial tab.
  const activeTab = is_test ? "test" : selectedTab;

  const url = makeUrl(event, custom_domain);
  const rtmpsServer = "rtmps://go.eventlive.pro:443/live";
  const server = rtmpsServer;
  const serverFull = `${server}/${stream_key}`;

  const test_stream_key = is_test ? stream_key : test_event?.stream_key;
  const test_url = is_test
    ? `https://evt.live/${broadcaster}/${slug}`
    : `https://evt.live/${test_event?.broadcaster}/${test_event?.slug}`;
  const serverFullTest = `${server}/${test_stream_key}`;
  const navigation = useNavigation();

  const now = useNow();
  const isBefore = now.isBefore(event_window_start_datetime_utc);

  // The event goes back to "upcoming" when the reset lands (the eventUpdated
  // subscription brings that in), which hides the button; resetDone hides it
  // straight away in case the update is slow.
  const [resetting, setResetting] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  useEffect(() => {
    if (event_status !== "past") setResetDone(false);
  }, [event_status]);
  const resetVideo = async () => {
    setResetting(true);
    try {
      const result = await resetEvent(slug);
      if (!result) throw new Error("No event returned");
      setResetDone(true);
      notify.success(
        t("Test video reset"),
        t("Your test is ready to stream again.")
      );
    } catch (err) {
      console.warn("resetEvent failed", err?.errors?.[0]?.message || err);
      notify.error(
        t("Couldn't reset the test video"),
        t("Try again in a moment.")
      );
    } finally {
      setResetting(false);
    }
  };

  const TabButton = ({ active, onPress, children }) => (
    <TouchableOpacity
      onPress={onPress}
      style={[
        tw`flex-1 py-3 px-4 items-center justify-center`,
        active ? tw`border-b-2 border-black` : tw`border-b border-gray-200`,
      ]}
    >
      <Text
        style={[
          tw`text-base font-medium`,
          active ? tw`text-black` : tw`text-gray-500`,
        ]}
      >
        {children}
      </Text>
    </TouchableOpacity>
  );

  return (
    <View>
      <View style={tw`flex-row border-b border-gray-200`}>
        {!is_test && (
          <TabButton
            active={activeTab === "live"}
            onPress={() => setActiveTab("live")}
          >
            {t("Live Event")}
          </TabButton>
        )}
        <TabButton
          active={activeTab === "test"}
          onPress={() => setActiveTab("test")}
        >
          {t("Free test")}
        </TabButton>
      </View>

      {activeTab === "live" && !is_test && (
        <View style={tw`relative my-4`}>
          {!is_published && !is_test && (
            <View
              style={tw`absolute inset-0 z-50 bg-black/50 flex justify-center items-center`}
            >
              <View style={tw`self-center`}>
                <Button
                  text={t("Unlock event to get your stream key")}
                  icon="lock-open-outline"
                  variant="primary"
                  size="md"
                  fullWidth={false}
                  onPress={() => {
                    navigation.navigate("PayWall", { event, slug });
                  }}
                />
              </View>
            </View>
          )}

          {displayStyle === "server_key" && (
            <>
              <FieldLabel
                label={t("RTMP server")}
                help="goLive.rtmpServer"
                style={tw`mt-4`}
              />
              <AppInput
                value={server}
                disabled={true}
                labelStyle={tw`text-right`}
                iconRightName={"copy-outline"}
                onIconRightAction={() => clipBoardCallback(server)}
              />

              <>
                <FieldLabel
                  label={t("Your event's private stream key")}
                  help="goLive.streamKey"
                  style={tw`mt-4`}
                />
                <AppInput
                  value={!is_published && !is_test ? "*****" : stream_key}
                  disabled={true}
                  labelStyle={tw`text-right`}
                  iconRightName={"copy-outline"}
                  onIconRightAction={() => clipBoardCallback(stream_key)}
                />
                {isBefore && (
                  <Callout tone="neutral" icon="time-outline">
                    {t(
                      "This stream key works from {date} at {time} {zone} (1 hour before the event starts). Until then, use the free test.",
                      {
                        date: moment(event_window_start_datetime_utc).format(
                          "LL"
                        ),
                        time: moment(event_window_start_datetime_utc).format(
                          "LT"
                        ),
                        zone: moment.tz(moment.tz.guess()).zoneAbbr(),
                      }
                    )}
                  </Callout>
                )}
              </>
            </>
          )}

          {displayStyle === "url" && (
            <>
              <FieldLabel
                label={t("RTMP URL")}
                help="goLive.rtmpServer"
                style={tw`mt-4`}
              />
              <AppInput
                value={serverFull}
                disabled={true}
                labelStyle={tw`text-right`}
                iconRightName={"copy-outline"}
                onIconRightAction={() => clipBoardCallback(serverFull)}
              />
              {isBefore && (
                <Callout tone="neutral" icon="time-outline">
                  {t(
                    "This URL works from {date} at {time} {zone} (1 hour before the event starts). Until then, use the free test.",
                    {
                      date: moment(event_window_start_datetime_utc).format(
                        "LL"
                      ),
                      time: moment(event_window_start_datetime_utc).format(
                        "LT"
                      ),
                      zone: moment.tz(moment.tz.guess()).zoneAbbr(),
                    }
                  )}
                </Callout>
              )}
            </>
          )}

          <UrlToWatch title={t("Link to watch your event")} url={url} />
        </View>
      )}

      {activeTab === "test" && (
        <View style={tw`my-4`}>
          {displayStyle === "server_key" && (
            <>
              <FieldLabel
                label={t("RTMP server")}
                help="goLive.rtmpServer"
                style={tw`mt-4`}
              />
              <AppInput
                value={server}
                disabled={true}
                labelStyle={tw`text-right`}
                iconRightName={"copy-outline"}
                onIconRightAction={() => clipBoardCallback(server)}
              />

              <FieldLabel
                label={t("Your test event's stream key")}
                help="goLive.freeTest"
                style={tw`mt-4`}
              />
              <AppInput
                value={test_stream_key}
                disabled={true}
                labelStyle={tw`text-right`}
                iconRightName={"copy-outline"}
                onIconRightAction={() => clipBoardCallback(test_stream_key)}
              />
            </>
          )}

          {displayStyle === "url" && (
            <>
              <FieldLabel
                label={t("RTMP URL")}
                help="goLive.rtmpServer"
                style={tw`mt-4`}
              />
              <AppInput
                value={serverFullTest}
                disabled={true}
                labelStyle={tw`text-right`}
                iconRightName={"copy-outline"}
                onIconRightAction={() => clipBoardCallback(serverFullTest)}
              />
            </>
          )}

          <UrlToWatch
            title={t("Your private test link")}
            url={test_url}
            isTest
          />

          <Text style={[settingsText.hint, tw`py-2.5`]}>
            {t("You can test live as many times as you need.")}
          </Text>
        </View>
      )}

      {event_status === "past" && is_test && !resetDone && (
        <View>
          <Button
            text={
              resetting ? t("Resetting test video…") : t("Reset test video")
            }
            icon="trash-outline"
            variant="danger-text"
            size="sm"
            loading={resetting}
            onPress={resetVideo}
          />
          <Text style={[settingsText.hint, tw`mt-1.5`]}>
            {t("Deletes the recording of your test so you can start over.")}
          </Text>
        </View>
      )}
    </View>
  );
};

export default RtmpForm;
